import { StyleType, CssColor, wixColor } from '@wix/editor-platform-ooi-metadata-resolver';
import { StyleParamType, wixColorParam, wixFontParam, IWixStyleFont, createStylesParam } from '@wix/tpa-settings';
import { Alignment } from '../../utils/alignment';

export enum TabSubtitlePosition {
  INLINE = 0,
  NEW_LINE = 1,
}

export const titleFont = (key: string) =>
  createStylesParam(key, {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      const { value: _, ...font } = wixFontParam('Body-M', {
        size: 12,
        style: { bold: true, italic: false, underline: false },
      })({ textPresets });
      return { ...font } as IWixStyleFont;
    },
  });

const subtitleFont = (key: string) =>
  createStylesParam(key, {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      const { value: _, ...font } = wixFontParam('Body-S', { size: 12 })({ textPresets });
      return { ...font } as IWixStyleFont;
    },
  });

const textColor = (key: string, isActive: boolean) =>
  createStylesParam(key, {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(isActive ? 'color-1' : 'color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(isActive ? CssColor.WstPrimaryBackgroundColor : CssColor.WstParagraph1Color),
    },
  });

const backgroundColor = (key: string, isActive: boolean) =>
  createStylesParam(key, {
    type: StyleParamType.Color,
    getDefaultValue: isActive ? wixColorParam('color-8') : wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: isActive ? CssColor.WstLinksAndActionsColor : CssColor.WstPrimaryBackgroundColor,
        opacity: isActive ? 1 : 0,
      },
    },
  });

export const cornerRadius = (key: string, defaultValue = 100) =>
  createStylesParam(key, {
    type: StyleParamType.Number,
    getDefaultValue: () => defaultValue,
    metadata: { type: StyleType.borderRadius, defaultValue: { type: 'static', value: defaultValue } },
  });

export const borderWidth = (key: string, defaultValue = 0) =>
  createStylesParam(key, {
    type: StyleParamType.Number,
    getDefaultValue: () => defaultValue,
    metadata: { type: StyleType.borderWidth, defaultValue: { type: 'static', value: defaultValue } },
  });

const borderColor = (key: string) =>
  createStylesParam(key, {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.2),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: CssColor.WstSystemLine1Color, opacity: 0.2 },
    },
  });

export const color = ({
  key,
  type,
  wixColorName,
  preset,
  opacity = 1,
}: {
  key: string;
  type: StyleType.color | StyleType.borderColor;
  wixColorName: string;
  preset: (typeof CssColor)[keyof typeof CssColor];
  opacity?: number;
}) =>
  createStylesParam(key, {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(wixColorName, opacity),
    metadata: { type, defaultValue: { type: 'static', preset, opacity } },
  });

export const padding = (key: string, defaultValue: number) =>
  createStylesParam(key, {
    type: StyleParamType.Number,
    getDefaultValue: () => defaultValue,
    metadata: { type: StyleType.padding, defaultValue: { type: 'static', value: defaultValue } },
  });

const gap = (key: string, defaultValue: number) =>
  createStylesParam(key, {
    type: StyleParamType.Number,
    getDefaultValue: () => defaultValue,
    metadata: { type: StyleType.gap, defaultValue: { type: 'static', value: defaultValue } },
  });

const margin = (key: string, defaultValue: number) =>
  createStylesParam(key, {
    type: StyleParamType.Number,
    getDefaultValue: () => defaultValue,
    metadata: { type: StyleType.margin, defaultValue: { type: 'static', value: defaultValue } },
  });

const alignment = (key: string) =>
  createStylesParam(key, {
    type: StyleParamType.Number,
    getDefaultValue: () => Alignment.CENTER,
    metadata: { type: StyleType.textAlign, defaultValue: { type: 'static', value: 'center' } },
  });

const subtitlePosition = (key: string) =>
  createStylesParam(key, {
    type: StyleParamType.Number,
    getDefaultValue: () => TabSubtitlePosition.INLINE,
  });

export const tabsStylesParams = {
  tabsGroupBackgroundColor: createStylesParam('tabs-group-background-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: CssColor.WstPrimaryBackgroundColor, opacity: 1 },
    },
  }),
  tabsGroupCornerRadius: cornerRadius('tabs-group-corner-radius'),
  tabsGroupBorderWidth: borderWidth('tabs-group-border-width'),
  tabsGroupBorderColor: borderColor('tabs-group-border-color'),

  tabsAlignment: alignment('tabs-alignment'),
  tabsVerticalSpacing: margin('tabs-vertical-spacing', 24),
  tabsGroupHorizontalPadding: padding('tabs-group-horizontal-padding', 6),
  tabsGroupVerticalPadding: padding('tabs-group-vertical-padding', 6),
  tabsSpacing: gap('tabs-spacing', 6),
  tabSubtitlePosition: subtitlePosition('tab-subtitle-position'),
  tabHorizontalPadding: padding('tab-horizontal-padding', 18),
  tabVerticalPadding: padding('tab-vertical-padding', 6),

  tabActiveDefaultTitleFont: titleFont('tab-active-default-title-font'),
  tabActiveDefaultTitleColor: textColor('tab-active-default-title-color', true),
  tabActiveDefaultSubtitleFont: subtitleFont('tab-active-default-subtitle-font'),
  tabActiveDefaultSubtitleColor: textColor('tab-active-default-subtitle-color', true),
  tabActiveDefaultBackgroundColor: backgroundColor('tab-active-default-background-color', true),
  tabActiveDefaultCornerRadiusTopLeft: cornerRadius('tab-active-default-corner-radius-top-left'),
  tabActiveDefaultCornerRadiusTopRight: cornerRadius('tab-active-default-corner-radius-top-right'),
  tabActiveDefaultCornerRadiusBottomLeft: cornerRadius('tab-active-default-corner-radius-bottom-left'),
  tabActiveDefaultCornerRadiusBottomRight: cornerRadius('tab-active-default-corner-radius-bottom-right'),
  tabActiveDefaultBorderWidth: borderWidth('tab-active-default-border-width'),
  tabActiveDefaultBorderColor: borderColor('tab-active-default-border-color'),

  tabActiveHoverTitleColor: textColor('tab-active-hover-title-color', true),
  tabActiveHoverSubtitleColor: textColor('tab-active-hover-subtitle-color', true),
  tabActiveHoverBackgroundColor: backgroundColor('tab-active-hover-background-color', true),
  tabActiveHoverBorderColor: borderColor('tab-active-hover-border-color'),

  tabInactiveDefaultTitleFont: titleFont('tab-inactive-default-title-font'),
  tabInactiveDefaultTitleColor: textColor('tab-inactive-default-title-color', false),
  tabInactiveDefaultSubtitleFont: subtitleFont('tab-inactive-default-subtitle-font'),
  tabInactiveDefaultSubtitleColor: textColor('tab-inactive-default-subtitle-color', false),
  tabInactiveDefaultBackgroundColor: backgroundColor('tab-inactive-default-background-color', false),
  tabInactiveDefaultCornerRadiusTopLeft: cornerRadius('tab-inactive-default-corner-radius-top-left'),
  tabInactiveDefaultCornerRadiusTopRight: cornerRadius('tab-inactive-default-corner-radius-top-right'),
  tabInactiveDefaultCornerRadiusBottomLeft: cornerRadius('tab-inactive-default-corner-radius-bottom-left'),
  tabInactiveDefaultCornerRadiusBottomRight: cornerRadius('tab-inactive-default-corner-radius-bottom-right'),
  tabInactiveDefaultBorderWidth: borderWidth('tab-inactive-default-border-width'),
  tabInactiveDefaultBorderColor: borderColor('tab-inactive-default-border-color'),

  tabInactiveHoverTitleColor: textColor('tab-inactive-hover-title-color', false),
  tabInactiveHoverSubtitleColor: textColor('tab-inactive-hover-subtitle-color', false),
  tabInactiveHoverBackgroundColor: backgroundColor('tab-inactive-hover-background-color', false),
  tabInactiveHoverBorderColor: borderColor('tab-inactive-hover-border-color'),
};
